import React from 'react';

import { Calendar as CalendarIcon, ChevronDown } from 'lucide-react';

export const ModernInput = ({
  label,
  icon: Icon,
  error,
  type = 'text',
  value,
  onChange,
  placeholder,
  className = '',
  labelClassName = '',
  wrapperClassName = '',
  inputClassName = '',
  iconClassName = '',
  required,
  ...props
}) => {
  return (
    <div className={className}>
      {label && (
        <label className={`block font-medium mb-2 text-gray-700 ${labelClassName}`}>
          {label} {required && '*'}
        </label>
      )}
      <div className={`flex items-center border-2 rounded-xl px-4 py-3 bg-white/70 transition-colors ${error ? 'border-red-400' : 'border-orange-200 focus-within:border-orange-400 focus-within:ring-2 focus-within:ring-orange-200'} ${wrapperClassName}`}>
        {Icon && <Icon className={`mr-3 h-5 w-5 ${error ? 'text-red-500' : 'text-orange-500'} ${iconClassName}`} />}
        <input type={type} value={value} onChange={onChange} placeholder={placeholder} required={required} className={`w-full outline-none bg-transparent text-gray-800 placeholder-gray-400 ${inputClassName}`} {...props} />
      </div>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
};

export const ModernSelect = ({
  label,
  icon: Icon,
  error,
  value,
  onChange,
  options = [],
  children,
  className = '',
  labelClassName = '',
  wrapperClassName = '',
  selectClassName = '',
  iconClassName = '',
  required,
  ...props
}) => {
  return (
    <div className={className}>
      {label && (
        <label className={`block font-medium mb-2 text-gray-700 ${labelClassName}`}>
          {label} {required && '*'}
        </label>
      )}
      <div className={`flex items-center border-2 rounded-xl px-4 py-3 bg-white/70 transition-colors relative ${error ? 'border-red-400' : 'border-orange-200 focus-within:border-orange-400 focus-within:ring-2 focus-within:ring-orange-200'} ${wrapperClassName}`}>
        {Icon && <Icon className={`mr-3 h-5 w-5 ${error ? 'text-red-500' : 'text-orange-500'} ${iconClassName}`} />}
        <select value={value} onChange={onChange} required={required} className={`w-full outline-none bg-transparent text-gray-800 appearance-none cursor-pointer ${selectClassName}`} {...props}>
          {children || options.map((opt, i) => (
            <option key={i} value={opt.value || opt.label}>{opt.label || opt.value}</option>
          ))}
        </select>
        <div className="absolute right-4 pointer-events-none text-orange-500">
          <ChevronDown className="w-5 h-5" />
        </div>
      </div>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
};

export const ModernDatePicker = ({
  label,
  value,
  onChange,
  min,
  language,
  className = '',
  labelClassName = '',
  wrapperClassName = '',
  inputClassName = '',
  required,
  ...props
}) => {
  return (
    <div className={className}>
      {label && (
        <label className={`block font-medium mb-2 text-gray-700 ${labelClassName}`}>
          {label} {required && '*'}
        </label>
      )}
      <div className="relative">
        <div className={`flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70 focus-within:border-orange-400 focus-within:ring-2 focus-within:ring-orange-200 ${wrapperClassName}`}>
          <CalendarIcon className="mr-3 h-5 w-5 text-orange-500" />
          <input type="date" value={value} min={min} onChange={onChange} required={required} className={`w-full outline-none bg-transparent text-gray-800 cursor-pointer ${inputClassName}`} {...props} />
        </div>
      </div>
    </div>
  );
};

export const TimeField = ({ label, value, onChange, step = 15, required, className = '' }) => {
  return (
    <div className={className}>
      {label && (
        <label className="block font-medium mb-2 text-gray-700">
          {label} {required && '*'}
        </label>
      )}
      <ModernSelect value={value} onChange={onChange} required={required}>
        {Array.from({ length: 96 }, (_, i) => {
          const h = Math.floor(i / 4);
          const m = (i % 4) * 15;
          const time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
          return <option key={time} value={time}>{time}</option>;
        })}
      </ModernSelect>
    </div>
  );
};

export const SelectInput = ModernSelect;
export const TextInput = ModernInput;
export const DateField = ModernDatePicker;

export const ModernCheckbox = ({ children, checked, onChange, error, className = '' }) => {
  return (
    <label className={`flex items-start gap-3 cursor-pointer ${className}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className={`mt-1 h-5 w-5 rounded border-2 accent-orange-600 ${error ? 'border-red-400' : 'border-orange-300'}`}
      />
      <div className="text-gray-700 leading-relaxed font-medium">
        {children}
      </div>
    </label>
  );
};

export const FormSection = ({
  title,
  subtitle,
  icon: Icon,
  children,
  className = '',
  headerClassName = '',
  titleClassName = '',
  subtitleClassName = '',
  iconWrapClassName = '',
}) => {
  return (
    <div className={`bg-white/80 backdrop-blur-sm rounded-3xl border border-orange-100 p-6 md:p-8 shadow-sm ${className}`}>
      <div className={`flex items-center gap-3 mb-6 ${headerClassName}`}>
        {Icon && (
          <div className={`p-3 bg-orange-100 text-orange-600 rounded-xl ${iconWrapClassName}`}>
            <Icon className="w-6 h-6" />
          </div>
        )}
        <div>
          <h2 className={`text-xl md:text-2xl font-bold text-gray-800 ${titleClassName}`}>{title}</h2>
          {subtitle && <p className={`text-sm text-gray-500 mt-1 ${subtitleClassName}`}>{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
};
